import { AccessGate } from '../../shared/access/AccessControls';
import { useAllowedMutation } from '../../shared/access/AccessControls';
import React, { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { signupRequestsApi } from '../../store/services/SignupRequestsService';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink,
  Button,
  Divider,
  Flex,
  Spinner,
} from '@chakra-ui/react';
import MainLayout from '../../shared/components/MainLayout';
import Card from '../../shared/components/ui/Card/Card';
import CardBody from '../../shared/components/ui/CardBody/CardBody';
import FormInput from '../../shared/components/FormInput';
import { SubmitHandler, useForm } from 'react-hook-form';
import { UpdateSignupRequestRequest } from '../../store/responseTypes/signupRequests';
import CardTitle from '../../shared/components/ui/CardTitle/CardTitle';
import { toPrettyDate } from '../../shared/utils/utils';
import { statusToColor } from '../signupRequests/helpers';
import AlertDialog from '../../shared/components/custom/AlertDialog/AlertDialog';
import { SignupRequestStatus } from '../../shared/constants/signupRequestStatus';
import { Paths } from '../../router/routes';

type ErrorData = { data: { details: string[] } }

const SignupRequestPage = () => {
  const { requestId } = useParams();

  const { data, isLoading } = signupRequestsApi.useGetSignupRequestByIdQuery({
    requestId: requestId || '',
  });

  const { register, handleSubmit, reset } = useForm<UpdateSignupRequestRequest>();
  const [updateSignupRequest, { isLoading: isUpdating, error: updateError }] =
    useAllowedMutation(signupRequestsApi.useUpdateSignupRequestMutation, 'updateSignupRequest');

  const updateErrorMessage = useMemo(() => {
    try {
      return (updateError as ErrorData)?.data?.details?.join(', ');
    } catch (error) {
      return 'Unknown error'
    }
  }, [updateError]);

  const signupRequest = useMemo(() => data?.signupRequest, [data]);

  useEffect(() => {
    reset({
      firstName: signupRequest?.firstName,
      lastName: signupRequest?.lastName,
      email: signupRequest?.email,
      phone: signupRequest?.phoneNumber,
    });
  }, [signupRequest]);

  const onSubmit: SubmitHandler<UpdateSignupRequestRequest> = async (args) => {
    if (!requestId) return;

    await updateSignupRequest({
      requestId: requestId,
      data: { ...args, type: signupRequest?.type ?? 1 },
    });
  };

  const [approveRequest, { error: approveError }] =
    useAllowedMutation(signupRequestsApi.useApproveSignupRequestMutation, 'approveSignupRequest');
  const [rejectRequest, { error: rejectError }] =
    useAllowedMutation(signupRequestsApi.useRejectSignupRequestMutation, 'rejectSignupRequest');

  const onApprove = () => {
    if (!signupRequest) return;
    approveRequest({ requestId: signupRequest?.id });
  };

  const onReject = () => {
    if (!signupRequest) return;
    rejectRequest({ requestId: signupRequest?.id });
  };

  const actionErrorMessage = useMemo(() => {
    try {
      return ((approveError || rejectError) as ErrorData)?.data?.details?.join(', ');
    } catch (error) {
      return 'Unknown error'
    }
  }, [rejectError, approveError]);

  return (
    <MainLayout>
      {isLoading && (
        <Flex w={'100%'} justifyContent={'center'}>
          <Spinner size="xl" />
        </Flex>
      )}

      {!!signupRequest && (
        <div>
          <Box mb={'2rem'}>
            <Breadcrumb>
              <BreadcrumbItem>
                <BreadcrumbLink as={Link} to={Paths.signupRequests}>Signup requests</BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink>{signupRequest.id}</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </Box>

          <Card>
            <CardTitle>
              <Badge colorScheme={statusToColor(signupRequest.status)}>
                {signupRequest.status}
              </Badge>
              <span style={{ marginLeft: '1rem' }}>Signup request from {toPrettyDate(signupRequest.createdAt, 'MMMM dd, yyyy HH:mm')}</span>
            </CardTitle>

            <CardBody>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box display={'grid'} gap={'1rem'}>
                  {!!signupRequest.companyName && (
                    <>
                      <Divider />
                      <p>Company name:</p>
                      <p><b>{signupRequest.companyName}</b></p>
                    </>
                  )}
                  <Divider />
                  <p>Request data:</p>
                  <FormInput ph={'First name'} name={'firstName'} register={register}></FormInput>
                  <FormInput ph={'Last name'} name={'lastName'} register={register}></FormInput>
                  <FormInput ph={'Email'} type={'email'} name={'email'} register={register}></FormInput>
                  <FormInput ph={'Phone'} name={'phone'} register={register}></FormInput>
                  <div>
                    <AccessGate action="ADMIN_SIGNUP_REQUEST_UPDATE"><Button isLoading={isUpdating} type={'submit'} colorScheme={'blue'}>Save</Button></AccessGate>
                  </div>
                  {!!updateError && (
                    <Alert status='error'>
                      <AlertIcon />
                      <AlertTitle>Update error</AlertTitle>
                      <AlertDescription>{updateErrorMessage}</AlertDescription>
                    </Alert>
                  )}
                  <Divider />
                  <p>Other actions</p>
                  <Flex gap={'0.5rem'}>
                    {signupRequest.status !== SignupRequestStatus.approved && (
                      <AccessGate action="ADMIN_SIGNUP_REQUEST_APPROVE"><AlertDialog
                        title={`Are you sure you want to APPROVE request of "${signupRequest.firstName}"`}
                        confirmLabel={'Approve'}
                        confirmColorScheme={'green'}
                        onConfirm={onApprove}
                        activator={
                          <Button type={'button'} colorScheme={'green'}>Approve</Button>
                        }
                      /></AccessGate>
                    )}
                    {signupRequest.status !== SignupRequestStatus.rejected && (
                      <AccessGate action="ADMIN_SIGNUP_REQUEST_REJECT"><AlertDialog
                        title={`Are you sure you want to REJECT request of "${signupRequest.firstName}"`}
                        confirmLabel={'Reject'}
                        confirmColorScheme={'red'}
                        onConfirm={onReject}
                        activator={
                          <Button type={'button'} colorScheme={'red'}>Reject</Button>
                        }
                      /></AccessGate>
                    )}
                  </Flex>
                  {!!actionErrorMessage && (
                    <Alert status='error'>
                      <AlertIcon />
                      <AlertTitle>Action error</AlertTitle>
                      <AlertDescription>{actionErrorMessage}</AlertDescription>
                    </Alert>
                  )}
                </Box>
              </form>
            </CardBody>
          </Card>
        </div>
      )}
    </MainLayout>
  );
};

export default SignupRequestPage;